import React, { useEffect, useState, useCallback } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    Typography,
    OutlinedInput,
    InputAdornment,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/material/styles';
import { IconSearch } from '@tabler/icons';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useHistory } from 'react-router-dom';
import debounce from 'lodash.debounce';
import * as XLSX from 'xlsx';
import api from '../../../../services/api';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1
});

const IMPORT_MAX_ROWS = 500;

// aceita cabeçalhos com/sem acento e maiúscula/minúscula
const HEADER_TO_FIELD = {
    'primeiro nome': 'first_name',
    nome: 'first_name',
    'first name': 'first_name',
    sobrenome: 'last_name',
    'ultimo nome': 'last_name',
    'last name': 'last_name',
    email: 'email',
    'e-mail': 'email',
    telefone: 'phone',
    phone: 'phone',
    cpf: 'cnpj',
    cnpj: 'cnpj',
    'cpf/cnpj': 'cnpj',
    'nome da organizacao': 'org_name',
    organizacao: 'org_name',
    'email da organizacao': 'org_email',
    'endereco da organizacao': 'org_address',
    'descricao da organizacao': 'org_description'
};

const normalizeHeader = (header) =>
    String(header || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '') // remove acentos
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ') // colapsa espaços duplos ("CNPJ  " -> "cnpj")
        .replace(/\s*\/\s*/g, '/'); // "CPF / CNPJ" e "CPF/ CNPJ" -> "cpf/cnpj"

// yyyy-mm-dd a partir das partes locais do Date (evita o typo clássico de
// usar toISOString(), que converte pra UTC e pode voltar um dia pro
// passado dependendo do fuso)
const formatDateCellValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// mesma lógica de `formatDateCellValue`, só que pra célula formatada como
// hora no Excel (o XLSX ainda devolve um Date - só a parte de hora importa)
const formatTimeCellValue = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const parseSpreadsheetRows = (workbook, fieldsConfig = []) => {
    const firstSheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[firstSheetName];
    const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true });

    const customFieldByHeader = {};
    fieldsConfig.forEach((f) => {
        customFieldByHeader[normalizeHeader(f.title)] = f;
    });

    return rawRows.map((rawRow) => {
        const mapped = {};
        const fields = {};
        Object.entries(rawRow).forEach(([header, value]) => {
            const normalized = normalizeHeader(header);
            const field = HEADER_TO_FIELD[normalized];
            if (field) {
                mapped[field] = String(value ?? '').trim();
                return;
            }
            const customField = customFieldByHeader[normalized];
            if (customField) {
                if (customField.type_field === 'date' && value instanceof Date) {
                    fields[customField.name] = formatDateCellValue(value);
                } else if (customField.type_field === 'time' && value instanceof Date) {
                    fields[customField.name] = formatTimeCellValue(value);
                } else {
                    fields[customField.name] = String(value ?? '').trim();
                }
            }
        });
        if (Object.keys(fields).length > 0) {
            mapped.fields = fields;
        }
        return mapped;
    });
};

const downloadUserTemplate = (fieldsConfig = []) => {
    const headers = [
        'Primeiro Nome',
        'Sobrenome',
        'Email',
        'Telefone',
        'CPF/CNPJ',
        'Nome da Organização',
        'Email da Organização',
        'Endereço da Organização',
        'Descrição da Organização',
        ...fieldsConfig.map((f) => f.title)
    ];
    const sheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Clientes');
    XLSX.writeFile(workbook, 'modelo_importacao_clientes.xlsx');
};

const useStyles = makeStyles((theme) => ({
    searchControl: {
        width: '434px',
        marginLeft: '16px',
        marginBottom: '8px',
        background: '#fff',
        [theme.breakpoints.down('lg')]: { width: '100%' }
    },
    popperResults: {
        position: 'absolute',
        zIndex: 1200,
        width: '434px',
        marginLeft: '16px',
        backgroundColor: '#fff',
        boxShadow: theme.shadows[3],
        maxHeight: '250px',
        overflowY: 'auto',
        borderRadius: '8px',
        [theme.breakpoints.down('lg')]: { width: '100%' }
    },
    listItemHover: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            cursor: 'pointer'
        }
    }
}));

const ViewUsers = () => {
    const [users, setUsers] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [appId, setAppId] = useState(null);

    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [importParseError, setImportParseError] = useState('');
    const [fieldsConfig, setFieldsConfig] = useState([]);

    const classes = useStyles();
    const history = useHistory();

    //  pega app padrão
    const pegar_app_padrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            setAppId(response.data.app.id);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        pegar_app_padrao();
    }, []);

    useEffect(() => {
        if (!appId) return;
        api.get(`v1/apps/${appId}/client_fields/`)
            .then((r) => setFieldsConfig(r.data || []))
            .catch(console.error);
    }, [appId]);

    // 🔥 só lista quando tiver appId
    useEffect(() => {
        if (!appId) return;
        listarUsuarios();
    }, [appId]);

    const listarUsuarios = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/users/`);
            setUsers(response.data.users || response.data);
        } catch (error) {
            console.error(error);
            setStatus({
                type: 'error',
                message: 'Erro ao listar usuários.'
            });
        }
    };

    const handleDeleteMany = async (userIds) => {
        if (!appId || userIds.length === 0) return;
        if (!window.confirm(`Tem certeza que deseja excluir ${userIds.length} usuário(s)? Essa ação não pode ser desfeita.`)) return;

        const results = await Promise.allSettled(userIds.map((id) => api.delete(`v1/apps/${appId}/users/${id}/`)));
        const failedCount = results.filter((r) => r.status === 'rejected').length;
        const succeededCount = results.length - failedCount;

        setStatus(
            failedCount > 0
                ? { type: 'warning', message: `${succeededCount} usuário(s) excluído(s), ${failedCount} falharam.` }
                : { type: 'success', message: `${succeededCount} usuário(s) excluído(s).` }
        );

        setSelectedUsers([]);
        listarUsuarios();
    };

    // 🔹 Importação de usuários via planilha
    const openImportDialog = () => {
        setImportResult(null);
        setImportParseError('');
        setImportDialogOpen(true);
    };

    const closeImportDialog = () => {
        if (importing) return;
        setImportDialogOpen(false);
    };

    const handleImportFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = ''; // permite selecionar o mesmo arquivo de novo depois
        if (!file || !appId) return;

        setImportResult(null);
        setImportParseError('');

        let rows;
        try {
            const buffer = await file.arrayBuffer();
            // cellDates precisa ser passado aqui (na leitura do workbook), não
            // no sheet_to_json - é o que faz uma célula formatada como data no
            // Excel virar um Date de verdade em vez do número de série interno
            // (ex: 45366); sem isso um campo customizado tipo "Data" importava
            // um número sem sentido, mesmo preenchido certinho.
            const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
            rows = parseSpreadsheetRows(workbook, fieldsConfig);
        } catch (error) {
            console.error('Erro ao ler planilha', error);
            setImportParseError('Não foi possível ler o arquivo. Confirme que é uma planilha .xlsx, .xls ou .csv válida.');
            return;
        }

        if (rows.length === 0) {
            setImportParseError('A planilha está vazia ou as colunas não foram reconhecidas.');
            return;
        }

        if (rows.length > IMPORT_MAX_ROWS) {
            setImportParseError(
                `A planilha tem ${rows.length} linhas - o limite é de ${IMPORT_MAX_ROWS} usuários por importação. Divida em arquivos menores.`
            );
            return;
        }

        try {
            setImporting(true);
            const response = await api.post(`v1/apps/${appId}/users/import/`, { users: rows });
            setImportResult(response.data);
            listarUsuarios();
        } catch (error) {
            console.error('Erro ao importar usuários', error);
            setImportParseError(error.response?.data?.error || 'Erro ao importar a planilha.');
        } finally {
            setImporting(false);
        }
    };

    const handleOpenUser = (user) => {
        setShowResults(false);
        history.push(`/dashboard/config/users/edit/${user.id}`);
    };

    // debounce DEPENDENDO do appId
    const handleSearch = useCallback(
        debounce(async (value, currentAppId) => {
            if (!value || !currentAppId) {
                setSearchResults([]);
                setShowResults(false);
                return;
            }

            try {
                const response = await api.get(`v1/apps/${currentAppId}/users/?search=${encodeURIComponent(value)}`);

                setSearchResults(response.data.users || response.data);
                setShowResults(true);
            } catch (error) {
                console.error('Erro na busca:', error);
                setStatus({
                    type: 'error',
                    message: 'Erro ao buscar usuários'
                });
            }
        }, 400),
        []
    );

    const handleChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (!appId) return;

        handleSearch(value, appId);
    };

    // Debounce utilitário para evitar spam na API
    function debounce(func, delay) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => func(...args), delay);
        };
    }

    const handleOpenOrganization = (organization) => {
        setShowResults(false);
        history.push(`/dashboard/config/organizations/edit/${organization.id}`);
    };

    return (
        <Box sx={{ position: 'relative' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h3" gutterBottom sx={{ mb: 0 }}>
                    Usuários
                </Typography>

                <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={openImportDialog}>
                    Importar usuários
                </Button>
            </Box>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            {/* Barra de busca */}
            <OutlinedInput
                className={classes.searchControl}
                id="input-search-header"
                value={searchTerm}
                onChange={handleChange}
                placeholder="Buscar por nome, CNPJ ou CPF..."
                startAdornment={
                    <InputAdornment position="start">
                        <IconSearch size="1.2rem" color="#999" />
                    </InputAdornment>
                }
            />

            {/* Resultados da busca em tempo real */}
            {showResults && searchResults.length > 0 && (
                <Paper className={classes.popperResults}>
                    <List dense>
                        {searchResults.map((user) => (
                            <ListItem key={user.id} className={classes.listItemHover} onClick={() => handleOpenUser(user)}>
                                <ListItemText
                                    primary={user.first_name}
                                    secondary={`${user.email || user.cpf || 'Sem documento'} — Organização: ${
                                        user.organization_name || 'Sem organização'
                                    }`}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            <Card sx={{ mt: 4 }}>
                <CardContent>
                    {/* Barra superior com ações em massa */}
                    {selectedUsers.length > 0 && (
                        <Box
                            sx={{
                                p: 2,
                                mb: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: '#f8f8f8',
                                borderRadius: 1
                            }}
                        >
                            <Typography>{selectedUsers.length} usuários selecionados</Typography>

                            <Button variant="contained" color="error" onClick={() => handleDeleteMany(selectedUsers)}>
                                Excluir Selecionados
                            </Button>
                        </Box>
                    )}

                    {/* Cabeçalho */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '50px 60px 1fr 1.5fr 1fr 1fr 1fr 0.1px',
                            fontWeight: 'bold',
                            pb: 1,
                            borderBottom: '2px solid #eee'
                        }}
                    >
                        <Checkbox
                            checked={selectedUsers.length === users.length && users.length > 0}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setSelectedUsers(users.map((u) => u.id));
                                } else {
                                    setSelectedUsers([]);
                                }
                            }}
                        />

                        <Typography></Typography>
                        <Typography>Nome</Typography>
                        <Typography>Email</Typography>
                        <Typography>Telefone</Typography>
                        {/* <Typography>Permissão</Typography> */}
                        <Typography>Organização</Typography>
                        <Typography>LOGO</Typography>
                        <Typography></Typography>
                    </Box>

                    {/* Linhas da tabela */}
                    {users.map((user) => (
                        <Box
                            key={user.id}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '50px 60px 1fr 1.5fr 1fr 1fr 1fr 0.1px',
                                borderBottom: '1px solid #f0f0f0',
                                py: 1,
                                alignItems: 'center',
                                cursor: 'pointer'
                            }}
                            onClick={(e) => {
                                if (e.target.tagName !== 'INPUT' && !e.target.dataset.org) {
                                    handleOpenUser(user);
                                }
                            }}
                        >
                            {/* Checkbox */}
                            <Checkbox
                                checked={selectedUsers.includes(user.id)}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    if (e.target.checked) {
                                        setSelectedUsers([...selectedUsers, user.id]);
                                    } else {
                                        setSelectedUsers(selectedUsers.filter((id) => id !== user.id));
                                    }
                                }}
                            />

                            {/* Avatar do usuário */}
                            <Box>
                                <img
                                    src={user.avatar || '/logo_user.jpg'}
                                    alt="avatar"
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </Box>

                            {/* Nome */}
                            <Typography>
                                {user.first_name} {user.last_name}
                            </Typography>

                            {/* Email */}
                            <Typography>{user.email || '—'}</Typography>

                            {/* Telefone */}
                            <Typography>{user.phone || '—'}</Typography>

                            {/* Permissão */}
                            {/* {user.role === 'customer' && <Typography>Usuário Final</Typography>}
                            {user.role === 'admin' && <Typography>Administrador</Typography>}
                            {user.role === 'agent' && <Typography>Membro da equipe</Typography>} */}

                            {/* Organização (CLICÁVEL) */}
                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (user.organization_id) {
                                        handleOpenOrganization({ id: user.organization_id });
                                    }
                                }}
                                data-org
                                sx={{
                                    cursor: user.organization_id ? 'pointer' : 'default',
                                    color: user.organization_id ? '#1976d2' : '#555',
                                    textDecoration: user.organization_id ? 'underline' : 'none'
                                }}
                            >
                                {user.organization_name || '—'}
                            </Box>

                            {/* Avatar da organização */}
                            <Box>
                                <img
                                    src={'/company.png'}
                                    alt="organization-logo"
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </Box>
                        </Box>
                    ))}
                </CardContent>
            </Card>

            {/* IMPORTAR USUÁRIOS */}
            <Dialog open={importDialogOpen} onClose={closeImportDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Importar usuários</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} mb={1}>
                            Como montar a planilha:
                        </Typography>
                        <Typography variant="body2" component="div">
                            A primeira linha deve conter os nomes das colunas. Colunas aceitas (não diferencia
                            maiúscula/minúscula nem acento):
                        </Typography>
                        <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2.5 }}>
                            <li>
                                <Typography variant="body2">
                                    <b>Primeiro Nome</b>, <b>Sobrenome</b> e <b>Email</b> — obrigatórios
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2">Telefone — opcional</Typography>
                            </li>
                            <li>
                                <Typography variant="body2">
                                    <b>CPF/CNPJ</b> — opcional. Se preenchido e já existir uma organização cadastrada
                                    com esse documento, o usuário é vinculado a ela automaticamente. Sem CPF/CNPJ, o
                                    usuário é importado sem organização vinculada.
                                </Typography>
                            </li>
                        </Box>
                        <Typography variant="body2" component="div" mb={0.5}>
                            Se a linha tiver CPF/CNPJ e ele <b>não</b> corresponder a nenhuma organização já cadastrada,
                            uma organização nova é criada na hora — nesse caso, essas colunas também ficam obrigatórias
                            nessa linha:
                        </Typography>
                        <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2.5 }}>
                            <li>
                                <Typography variant="body2">
                                    <b>Nome da Organização</b> e <b>Email da Organização</b> — obrigatórios
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2">
                                    Endereço da Organização, Descrição da Organização — opcionais
                                </Typography>
                            </li>
                        </Box>
                        {fieldsConfig.length > 0 && (
                            <>
                                <Typography variant="body2" component="div" mb={0.5}>
                                    Campos customizados do cliente:
                                </Typography>
                                <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2.5 }}>
                                    {fieldsConfig.map((f) => (
                                        <li key={f.id}>
                                            <Typography variant="body2">
                                                <b>{f.title}</b> — {f.required ? 'obrigatório' : 'opcional'}
                                                {f.type_field === 'boolean' ? ' — aceita "Sim"/"Não" ou "true"/"false"' : ''}
                                                {f.type_field === 'date' ? ' — formato AAAA-MM-DD (ex: 2024-03-15) ou célula formatada como data' : ''}
                                                {f.type_field === 'time' ? ' — formato HH:MM (ex: 14:30) ou célula formatada como hora' : ''}
                                                {f.type_field === 'select' ? ` — valores aceitos: ${(f.options || []).join(', ')}` : ''}
                                            </Typography>
                                        </li>
                                    ))}
                                </Box>
                            </>
                        )}
                        <Typography variant="body2">
                            Formatos aceitos: <b>.xlsx</b>, <b>.xls</b> ou <b>.csv</b>. Limite de <b>{IMPORT_MAX_ROWS} usuários</b> por
                            importação — planilhas maiores devem ser divididas em arquivos menores.
                        </Typography>
                    </Alert>

                    <Button
                        variant="outlined"
                        startIcon={<CloudDownloadIcon />}
                        fullWidth
                        sx={{ mb: 2 }}
                        onClick={() => downloadUserTemplate(fieldsConfig)}
                    >
                        Baixar arquivo Excel de modelo
                    </Button>

                    {importParseError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {importParseError}
                        </Alert>
                    )}

                    {importResult && (
                        <Alert severity={importResult.error_count > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={600}>
                                {importResult.created_count} usuário(s) importado(s) com sucesso.
                            </Typography>
                            {importResult.error_count > 0 && (
                                <>
                                    <Typography variant="body2" mt={1}>
                                        {importResult.error_count} linha(s) não importada(s):
                                    </Typography>
                                    <List dense>
                                        {importResult.errors.map((err) => (
                                            <ListItem key={err.row} disableGutters>
                                                <ListItemText
                                                    primary={`Linha ${err.row}${err.name ? ` (${err.name})` : ''}: ${err.error}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}
                        </Alert>
                    )}

                    <Button
                        component="label"
                        variant="outlined"
                        startIcon={importing ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                        disabled={importing}
                        fullWidth
                    >
                        {importing ? 'Importando...' : 'Escolher planilha'}
                        <VisuallyHiddenInput type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} />
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeImportDialog} disabled={importing}>
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Lista completa (abaixo da busca) */}
            {/* <Card sx={{ mt: 4 }}>
                <CardContent>
                    <List>
                        <ListItemText variant="h6" gutterBottom style={{ fontWeight: 'bold', padding: '16px', alignContent: 'left' }}>
                            Nome
                        </ListItemText>
                        <ListItemText variant="h6" gutterBottom style={{ fontWeight: 'bold', padding: '16px', alignContent: 'left' }}>
                            Email
                        </ListItemText>
                        <ListItemText variant="h6" gutterBottom style={{ fontWeight: 'bold', padding: '16px', alignContent: 'left' }}>
                            Telefone
                        </ListItemText>
                        {users.map((user) => (
                            <ListItem key={user.id} button onClick={() => handleOpenUser(user)}>
                                <ListItemText
                                    primary={user.name}
                                    secondary={
                                        <Box>
                                            <Typography variant="body2" color="textSecondary">
                                                {user.first_name || user.username || 'Sem Nome'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {user.email || 'Sem Email'}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card> */}
        </Box>
    );
};

export default ViewUsers;
